import { useEffect, useRef, useState } from 'react';
import { SuccessStoriesService } from '../services/successStories';

const EMBED_SRC = '//www.instagram.com/embed.js';

const ensureInstagramEmbedScript = () => {
  const existing = document.querySelector(`script[src="${EMBED_SRC}"]`);
  if (existing) {
    if (window.instgrm) window.instgrm.Embeds.process();
    return;
  }

  const script = document.createElement('script');
  script.src = EMBED_SRC;
  script.async = true;
  script.onload = () => {
    if (window.instgrm) window.instgrm.Embeds.process();
  };
  document.body.appendChild(script);
};

// Shown until an admin has published at least one story from the dashboard,
// so the section is never empty during rollout.
const FALLBACK_URLS = [
  'https://www.instagram.com/reel/DKrXJT0pZep/?igsh=a2Z0a3VweW9uajBq',
  'https://www.instagram.com/reel/DKgUNcbpH7w/?igsh=bWUyYmx1anZkdWY1',
  'https://www.instagram.com/reel/DJ1qV2uJaG_/?igsh=dTBkMzBsMHRoYXd4',
  'https://www.instagram.com/reel/DKPRkArp7qU/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==',
];

// Instagram's embed script fully hydrates into an iframe with a username
// header on top and a likes/comment-box footer on the bottom, which we don't
// want shown (and can't reach inside — it's a cross-origin iframe, so we
// can't hide pieces of it directly). Instead we measure the embed's natural
// (unscaled) size once Instagram has rendered it, then crop a fixed amount
// off the top (header) and bottom (footer) via a pixel-space translate, and
// scale the whole thing down to fit the card's actual width — so only the
// media + action-icon row shows, at exactly the card's width, no scrollbar.
const HEADER_CROP_PX = 62; // Instagram's avatar/username row height
// Likes-count row + "Add a comment" row height. Kept deliberately a bit
// short of the full measured chrome height — cropping too aggressively here
// risks slicing into the action-icon row just above it, which looks far more
// broken than leaving a sliver of the likes text peeking through.
const FOOTER_CROP_PX = 78;
const HYDRATED_MIN_HEIGHT_PX = 400; // below this, assume no header/footer to crop yet

function InstagramReelCard({ url, index }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [naturalSize, setNaturalSize] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const iframeTitle = `Student testimonial video ${index + 1}`;

    const measure = () => {
      if (cancelled || !measureRef.current) return;
      // embed.js injects its iframe with an empty title, which screen readers
      // announce as an unlabeled frame; give it a descriptive one.
      const iframe = measureRef.current.querySelector('iframe');
      if (iframe && iframe.getAttribute('title') !== iframeTitle) {
        iframe.setAttribute('title', iframeTitle);
      }
      const w = measureRef.current.scrollWidth;
      const h = measureRef.current.scrollHeight;
      if (w > 0 && h > 0) {
        setNaturalSize((prev) => (prev && prev.width === w && prev.height === h ? prev : { width: w, height: h }));
      }
    };

    const resizeObserver = new ResizeObserver(measure);
    const mutationObserver = new MutationObserver(measure);
    if (measureRef.current) {
      resizeObserver.observe(measureRef.current);
      mutationObserver.observe(measureRef.current, { childList: true, subtree: true, attributes: true });
    }
    // Instagram's embed.js populates the blockquote asynchronously (and sometimes
    // in more than one pass); keep re-measuring for a few seconds to catch that.
    const timeouts = [200, 600, 1200, 2200, 3500].map((ms) => setTimeout(measure, ms));

    return () => {
      cancelled = true;
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      timeouts.forEach(clearTimeout);
    };
  }, [url, index]);

  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current || !naturalSize?.width) return;
      const availableWidth = containerRef.current.clientWidth;
      if (availableWidth > 0) {
        setScale(Math.min(1, availableWidth / naturalSize.width));
      }
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [naturalSize]);

  const isHydrated = naturalSize ? naturalSize.height > HYDRATED_MIN_HEIGHT_PX : false;
  const topCrop = isHydrated ? HEADER_CROP_PX : 0;
  const bottomCrop = isHydrated ? FOOTER_CROP_PX : 0;
  const visibleNaturalHeight = naturalSize ? Math.max(1, naturalSize.height - topCrop - bottomCrop) : null;
  const scaledHeight = visibleNaturalHeight ? visibleNaturalHeight * scale : null;

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden rounded-[20px] bg-white shadow-[0_15px_30px_rgba(0,0,0,0.15)] hover:shadow-[0_25px_50px_rgba(0,0,0,0.25)] hover:-translate-y-3 transition-all duration-300 border border-white/20"
      style={{ height: scaledHeight ? `${scaledHeight}px` : '370px' }}
    >
      <div
        ref={measureRef}
        style={{ display: 'inline-block', transform: `scale(${scale}) translateY(-${topCrop}px)`, transformOrigin: 'top left' }}
      >
        <blockquote
          className="instagram-media"
          data-instgrm-permalink={url}
          data-instgrm-version="14"
          style={{ background: '#FFF', border: 0, borderRadius: '3px', boxShadow: 'none', margin: 0, padding: 0, width: '326px' }}
        />
      </div>
    </div>
  );
}

const SuccessStoriesSection = ({
  insetClassName = 'mx-3 sm:mx-6 md:mx-10 lg:mx-16',
  cardClassName = 'rounded-[40px]',
}) => {
  const [urls, setUrls] = useState(FALLBACK_URLS);
  const sectionRef = useRef(null);
  // embed.js pulls in ~2MB of Instagram CSS/images, so hold it back until the
  // section is close to the viewport instead of loading it with the page.
  const [nearViewport, setNearViewport] = useState(
    () => typeof window === 'undefined' || !('IntersectionObserver' in window)
  );

  useEffect(() => {
    if (nearViewport || !sectionRef.current) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setNearViewport(true);
        observer.disconnect();
      }
    }, { rootMargin: '400px 0px' });
    observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [nearViewport]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await SuccessStoriesService.listPublic();
        const fetchedUrls = (Array.isArray(data) ? data : [])
          .map((story) => story?.instagram_url)
          .filter(Boolean);
        if (!cancelled && fetchedUrls.length) setUrls(fetchedUrls);
      } catch (error) {
        console.error('Failed to load success stories:', error);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-run whenever the URL list changes (including the first async load),
  // not just once on mount, so newly-arrived embeds actually get processed.
  useEffect(() => {
    if (nearViewport) ensureInstagramEmbedScript();
  }, [urls, nearViewport]);

  return (
    <section ref={sectionRef} className="pt-4 pb-6 px-0 mt-4">
      <div className={`${insetClassName} bg-[#EFF6FF] ${cardClassName} py-10 px-2 sm:px-4 md:px-6 shadow-2xl`}>
        <div className="text-center mb-6 px-2">
          <h2 className="text-[36px] font-semibold font-['Poppins'] leading-[48px] tracking-[-0.5px]">
            <span className="font-['Poppins'] text-[#011832]">Success</span>{' '}
            <span className="font-['Poppins'] bg-gradient-to-r from-[#F9793A] to-[#F75709] bg-clip-text text-transparent">Stories</span>
          </h2>
          <p className="mt-1 text-[#0E2A46] text-[16px] font-['Inter'] font-normal leading-[30px] tracking-[0.5px] text-center">
            More than students, they are our family, our pride, and our greatest story.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {urls.map((url, index) => (
            <InstagramReelCard key={`${url}-${index}`} url={url} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SuccessStoriesSection;
